import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  Input,
  Self,
  TemplateRef,
  Optional,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { AbstractControl, ControlValueAccessor, UntypedFormControl, NgControl, ValidationErrors } from "@angular/forms";
import { Observable, BehaviorSubject } from "rxjs";

import { UniFormFieldControl } from "../fom-field/form-field-control";

import { UniLabelTemplateDirective, UniOptionTemplateDirective } from "./templates.directive";

let nextUniqueId = 0;

type SelectedValue = string | Record<string, unknown>;

@Component({
  selector: "uni-select",
  templateUrl: "./select.component.html",
  providers: [{ provide: UniFormFieldControl, useExisting: UniSelectComponent }],
})
export class UniSelectComponent implements AfterViewInit, ControlValueAccessor, UniFormFieldControl<any>, OnChanges {
  public selectControl = new UntypedFormControl();

  /** Implemented as part of UniFormFieldControl. */
  public get statusChanges(): Observable<string> {
    return this.selectControl.statusChanges;
  }

  public get errors(): ValidationErrors | null {
    return this.selectControl.errors;
  }

  public valuesChanges = new BehaviorSubject<SelectedValue>(null);

  public get id(): string {
    return this._id || this._uid;
  }
  @Input()
  public set labelForId(value: string) {
    this._id = value || this._uid;
  }
  protected _id: string;
  protected _uid = `uni-select-${nextUniqueId++}`;

  public get value(): SelectedValue {
    return this.selectControl.value;
  }

  public set value(value: SelectedValue) {
    this.selectControl.setValue(value);
  }

  @Input()
  public get required(): boolean {
    const validators = this.ngControl?.control?.validator;
    return this._required ?? validators ? validators({} as AbstractControl)?.hasOwnProperty("required") : false;
  }
  public set required(value: boolean) {
    this._required = value;
  }
  protected _required: boolean | undefined;

  public disabled: boolean;
  public invalid: boolean;

  @Input() public loading: boolean = false;
  @Input() public placeholder: string = "Seleccionar";
  @Input() public bindLabel: string;
  @Input() public bindValue: string;
  @Input() public multiple: boolean = false;

  @Input() public virtualScroll: boolean = false;
  @Input() public clearable = true;
  @Input() public closeOnSelect = true;
  @Input() public autoFill = true;
  @Input() public selectOption: number;

  @Input() public set items(newItems: SelectedValue[]) {
    this._items = newItems;
    if (this._items?.length === 1 && this.autoFill) {
      this.value = this.bindValue ? this._items[0][this.bindValue] : this._items[0];
      this.valuesChanges.next(this.value);
    }
  }
  public get items(): SelectedValue[] {
    return this._items;
  }

  protected _items: SelectedValue[];

  @ContentChild(UniOptionTemplateDirective, { read: TemplateRef }) public optionTemplate: TemplateRef<unknown>;
  @ContentChild(UniLabelTemplateDirective, { read: TemplateRef }) public labelTemplate: TemplateRef<unknown>;

  constructor(@Optional() @Self() public ngControl: NgControl, public _elementRef: ElementRef) {
    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }

  public ngAfterViewInit(): void {
    this.selectControl.valueChanges.subscribe((newValue) => {
      this.onChange?.(newValue);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.items?.currentValue && this.selectOption !== undefined) {
      this.value = this.bindValue ? this._items[this.selectOption][this.bindValue] : this._items[this.selectOption];
      this.valuesChanges.next(this.value);
    }
  }

  public onTouched = Function.prototype;
  public onChange;

  public writeValue(value?: SelectedValue): void {
    if (value !== undefined) {
      this.value = value;
    }
  }

  public registerOnChange(fn: (value: SelectedValue) => void): void {
    this.onChange = fn;
    this.valuesChanges.subscribe((newValue: any) => {
      if ((!newValue && this.selectControl.touched) || newValue) {
        fn(newValue);
      }
    });
  }

  private getFormBindedValue(newValue: any): string | Record<string, any> {
    return this.bindValue && newValue ? newValue[this.bindValue] : newValue;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.selectControl?.disable() : this.selectControl?.enable();
  }
}
